/**
 * **S3 的信封与结构化错误。**
 *
 * 这一层只认识"消息"，不认识"游戏" —— 它校验的是**信封**
 * （谁发给谁、是请求还是通知），载荷的业务形状由注册方法时给的 zod schema 负责。
 *
 * ## 为什么线上跑的是 JSON **字符串**，不是对象
 *
 * Trystero 的 action 可以直传对象（结构化克隆），那样更快。这里仍然选字符串：
 *
 * 1. 校验阶梯（文本 → JSON → 信封 → 载荷）是**真的**存在的一级，
 *    不是装饰。对象进来就没有 `BadJSON` 这一档了。
 * 2. `PARDON_ME` 要带 `rawText` 回去 —— 没有文本就没法说"我收到的是这个"。
 * 3. 换传输（WebSocket / BroadcastChannel / 内存假实现）时线格式不变。
 *
 * 代价是每帧多一次 `JSON.parse`，相对 WebRTC 可以忽略。
 *
 * ## `PARDON_ME` 是从 `old_repo` 照抄的**唯一**设计（见
 * `docs/archive/netcode-s3-evaluation.md` §3①）
 *
 * 远端来的东西坏了，**必须留下能查的痕迹**，绝不静默丢弃。
 * 一条带 `reason` 枚举 + `details` + `rawText` 的报障单，
 * 比"消息莫名消失"节省的调试时间高几个数量级。
 */

import { z } from 'zod';
import { t, type TextKey } from '../i18n';

/** 信封版本。载荷 schema 变了不算改版本 —— 那是各方法自己的事 */
export const S3_VERSION = 1;

// ============================================================
// PARDON_ME
// ============================================================

/**
 * 报障原因枚举。键名就是线格式，值是给人看的说明。
 *
 * ⚠️ **这 12 个不是随便凑的**，它们一一对应校验阶梯上的每一级
 * （见 `docs/netcode-s3.md` §5.1）。加一个原因之前先问：
 * 它是阶梯上某一级的失败吗？如果不是，那它应该是业务错误（`{ok:false,reason}`），
 * 而不是协议错误。
 */
// ⚠️ 值是 **i18n 键**，不是文案：PARDON 是纯协议模块，不在这里渲染。
//    要给人看时调 `pardonText()`。这样 reason 的**键**（BadData…）仍然是线格式。
export const PARDON = Object.freeze({
  /** 载荷根本不是字符串 */
  BadData: 'host.pardon.badData',
  /** `JSON.parse` 失败 */
  BadJSON: 'host.pardon.badJson',
  /** 不是合法信封 */
  BadMessage: 'host.pardon.badMessage',
  /** 走请求通道但不是 `req` */
  BadRequest: 'host.pardon.badRequest',
  /** 响应不满足信封接口 */
  BadResponse: 'host.pardon.badResponse',
  /** `t` 不认识 */
  BadMessageType: 'host.pardon.badMessageType',
  /** 方法没注册 */
  BadMethod: 'host.pardon.badMethod',
  /** 载荷不满足业务 schema */
  BadPayload: 'host.pardon.badPayload',
  /** 分派本身抛了（我们自己的 bug） */
  InternalError: 'host.pardon.internalError',
  /** 业务 handler 抛了 */
  HandlerError: 'host.pardon.handlerError',
  /** 响应找不到在飞的请求 */
  OrphanResponse: 'host.pardon.orphanResponse',
  /** 业务 handler 明确拒绝 */
  AccessDenied: 'host.pardon.accessDenied',
} satisfies Record<string, TextKey>);

export type PardonReason = keyof typeof PARDON;

/** 报障原因 → 给人看的一句话。协议层只存键，渲染时才翻译 */
export function pardonText(reason: PardonReason): string {
  return t(PARDON[reason]);
}

const PARDON_REASONS = Object.keys(PARDON) as [PardonReason, ...PardonReason[]];

// ============================================================
// 信封
// ============================================================

const BaseEnvelopeSchema = z.object({
  /** 协议版本。对不上会被 `BadMessage` 拦下 —— 这是刻意的：
   *  宁可吵着失败，也不要拿旧格式去解新代码 */
  s3: z.literal(S3_VERSION),
  /** 发送方本地时间，**只用于日志**，不参与任何判定 */
  ts: z.number(),
});

export const RequestEnvelopeSchema = BaseEnvelopeSchema.extend({
  t: z.literal('req'),
  id: z.string().min(1),
  method: z.string().min(1),
  payload: z.unknown(),
});

export const ResponseEnvelopeSchema = BaseEnvelopeSchema.extend({
  t: z.literal('res'),
  id: z.string().min(1),
  /**
   * 响应体。**业务的否决也走这里** —— 例如 `{ ok: false, reason: '现在不是你的回合' }`。
   *
   * ⚠️ 信封上**没有** `ok` 字段是刻意的：业务否决不是协议故障，
   * 它应该长得像"一次成功的 RPC，只是答案是'不行'"。
   * 真正的故障（校验失败、handler 抛了、权限不够）走 `pardon` 信封。
   */
  payload: z.unknown(),
});

export const MessageEnvelopeSchema = BaseEnvelopeSchema.extend({
  t: z.literal('msg'),
  method: z.string().min(1),
  payload: z.unknown(),
});

export const PardonEnvelopeSchema = BaseEnvelopeSchema.extend({
  t: z.literal('pardon'),
  /** 相关联的请求 id；不是响应某个请求时为 `null` */
  id: z.string().nullable(),
  reason: z.enum(PARDON_REASONS),
  details: z.unknown().optional(),
  /** 收到的原始文本。解析都没成功时为 `null` */
  rawText: z.string().nullable(),
  parseFailed: z.boolean(),
});

export const EnvelopeSchema = z.discriminatedUnion('t', [
  RequestEnvelopeSchema,
  ResponseEnvelopeSchema,
  MessageEnvelopeSchema,
  PardonEnvelopeSchema,
]);

export type RequestEnvelope = z.infer<typeof RequestEnvelopeSchema>;
export type ResponseEnvelope = z.infer<typeof ResponseEnvelopeSchema>;
export type MessageEnvelope = z.infer<typeof MessageEnvelopeSchema>;
export type PardonEnvelope = z.infer<typeof PardonEnvelopeSchema>;
export type Envelope = z.infer<typeof EnvelopeSchema>;

// ============================================================
// 编码
// ============================================================

let counter = 0;

/**
 * 请求 id。
 *
 * 只需要在**一条连接的往返窗口内**不重复，不需要全局唯一 ——
 * 所以"自增计数 + 一点随机"就够，用不着 `crypto.randomUUID`
 * （本项目明确不做安全设计，也没必要为它引入依赖）。
 */
export function nextMessageId(): string {
  counter = (counter + 1) % Number.MAX_SAFE_INTEGER;
  return `${counter.toString(36)}-${Math.floor(Math.random() * 0xffffff).toString(36)}`;
}

/** 供测试复位计数器，让请求 id 可预测 */
export function resetMessageIds(): void {
  counter = 0;
}

export function encodeRequest(id: string, method: string, payload: unknown): string {
  const env: RequestEnvelope = { s3: S3_VERSION, t: 'req', ts: Date.now(), id, method, payload };
  return JSON.stringify(env);
}

/**
 * 编码响应。`undefined` 会被写成 `null` —— 因为"没有载荷"在 JSON 里
 * 就是 `null`，而 schema 上要求 `payload` 这个键**存在**。
 * 对应地，没有响应的方法的响应 schema 写 `z.null()`。
 */
export function encodeResponse(id: string, payload?: unknown): string {
  const env: ResponseEnvelope = { s3: S3_VERSION, t: 'res', ts: Date.now(), id, payload: payload === undefined ? null : payload };
  return JSON.stringify(env);
}

export function encodeMessage(method: string, payload: unknown): string {
  const env: MessageEnvelope = { s3: S3_VERSION, t: 'msg', ts: Date.now(), method, payload };
  return JSON.stringify(env);
}

export function encodePardon(input: PardonInput): string {
  const env: PardonEnvelope = {
    s3: S3_VERSION,
    t: 'pardon',
    ts: Date.now(),
    id: input.id ?? null,
    reason: input.reason,
    details: input.details,
    rawText: input.rawText ?? null,
    parseFailed: input.parseFailed ?? false,
  };
  return JSON.stringify(env);
}

export interface PardonInput {
  readonly reason: PardonReason;
  /** 相关联的请求 id */
  readonly id?: string | null;
  readonly details?: unknown;
  readonly rawText?: string | null;
  readonly parseFailed?: boolean;
}

// ============================================================
// 解码（校验阶梯的前三级）
// ============================================================

export type DecodeResult =
  | { readonly ok: true; readonly envelope: Envelope }
  | { readonly ok: false; readonly pardon: PardonInput };

/**
 * 文本 → 信封。**这是校验阶梯的前三级**：
 *
 * ```
 * 不是 string → BadData → JSON.parse 失败 → BadJSON → 信封不合 schema → BadMessage
 * ```
 *
 * 注意它**不抛异常**，而是把失败也表达成一个值（`PardonInput`）——
 * 因为调用方要做的事情不是"中断"，而是"回一张报障单"。
 */
export function decodeEnvelope(raw: unknown): DecodeResult {
  if (typeof raw !== 'string') {
    return { ok: false, pardon: { reason: 'BadData', details: t('host.protocol.payloadType', { type: typeof raw }), rawText: null, parseFailed: true } };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ok: false, pardon: { reason: 'BadJSON', details: describeError(error), rawText: raw, parseFailed: true } };
  }
  const result = EnvelopeSchema.safeParse(parsed);
  if (!result.success) {
    return { ok: false, pardon: { reason: 'BadMessage', details: z.treeifyError(result.error), rawText: raw } };
  }
  return { ok: true, envelope: result.data };
}

// ============================================================
// 错误
// ============================================================

/**
 * 协议层与传输层错误的**统一出口**。
 *
 * `kind` 把三件事并成一个词表：PARDON 的 12 个 reason，
 * 加上传输的四种结局。调用方只需要 switch 一次。
 */
export type S3ErrorKind = PardonReason | 'Timeout' | 'Disconnected' | 'Aborted' | 'RemoteRejected';

export class S3Error extends Error {
  readonly kind: S3ErrorKind;
  readonly details: unknown;

  constructor(kind: S3ErrorKind, message: string, details?: unknown) {
    super(message);
    this.name = 'S3Error';
    this.kind = kind;
    this.details = details;
  }
}

/**
 * **业务 handler 抛这个 = 回一条 `AccessDenied` 报障单。**
 *
 * 其它异常一律变成 `HandlerError` —— 区分这两者是刻意的：
 * "你不该做这件事"和"我写崩了"在排障时是完全不同的两件事。
 */
export class AccessDeniedError extends Error {
  constructor(message = 'Access Denied') {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

export function describeError(error: unknown): unknown {
  if (error instanceof Error) return { name: error.name, message: error.message, stack: error.stack };
  return error;
}
